import apiConfig from '@src/Configs/apiConfig';

/**
 * Per-vendor consent state. Each flag reflects whether the user has granted
 * Didomi consent for that vendor's purpose. Adapters check their own flag
 * before sending data.
 */
export interface VendorConsent {
  amplitude: boolean;
  braze: boolean;
  appsflyer: boolean;
  /** Backend relay is necessary-cookie tier: never strictly blocked, but
   * disabled if the user has rejected ALL purposes. */
  backendRelay: boolean;
}

const USER_ID_KEY = 'trace_user_id';

/**
 * Logged-in detection. We treat a user as "logged in" once a stable user_id
 * has been persisted by the identify() flow (LayoutProvider).
 *
 * Anonymous (logged-out) users are auto-consented on web — consent gating
 * applies only to logged-in members so the signed-in experience honors the
 * user's explicit Didomi choices.
 */
const isLoggedIn = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(USER_ID_KEY);
  } catch {
    return false;
  }
};

const didomiPurposesEnabled = (): string[] => {
  if (typeof window === 'undefined' || typeof window.Didomi === 'undefined') {
    return [];
  }
  try {
    return window.Didomi.getUserConsentStatusForAll().purposes.enabled || [];
  } catch {
    return [];
  }
};

/**
 * Whether Didomi determines consent must be collected for this session (i.e.
 * the user is in a jurisdiction/config where consent is legally required).
 *
 * Defaults to `true` (conservative) when Didomi has not loaded yet or the call
 * throws, so we never send before we know consent is not required. Outside a
 * consent-required jurisdiction Didomi returns false, and we then treat the
 * member as consented rather than silently dropping all analytics.
 */
const isConsentRequired = (): boolean => {
  if (typeof window === 'undefined' || typeof window.Didomi === 'undefined') {
    return true;
  }
  try {
    return typeof window.Didomi.isConsentRequired === 'function'
      ? window.Didomi.isConsentRequired()
      : true;
  } catch {
    return true;
  }
};

export const getVendorConsent = (): VendorConsent => {
  // Logged-out (anonymous) users: auto-consent so we can track baseline
  // engagement without forcing them through a consent flow first. Logged-in
  // members go through the proper Didomi check below.
  if (!isLoggedIn()) {
    return {
      amplitude: true,
      braze: true,
      appsflyer: true,
      backendRelay: true,
    };
  }

  // Logged-in member, but consent is NOT required for this session (e.g. outside
  // GDPR scope). Treat them as consented instead of dropping all analytics —
  // there is no banner to grant it and no legal need to gate it here.
  if (!isConsentRequired()) {
    return {
      amplitude: true,
      braze: true,
      appsflyer: true,
      backendRelay: true,
    };
  }

  // Consent IS required: honor the member's explicit Didomi purpose grants.
  const enabled = didomiPurposesEnabled();
  const has = (id: string) => !!id && enabled.includes(id);
  return {
    amplitude: has(apiConfig.amplitudePurpId),
    braze: has(apiConfig.brazeId),
    appsflyer: has(apiConfig.appsFlyerPurpId),
    backendRelay: enabled.length > 0,
  };
};

/**
 * Legacy boolean: keep for backward compatibility with code that just
 * checks "is Amplitude allowed".
 */
export const isTrackingEnabled = (): boolean => getVendorConsent().amplitude;
